import { AfterViewInit, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import {
  ActionSheetController,
  IonDatetime,
  ModalController,
} from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { FileSharer } from '@byteowls/capacitor-filesharer';
import FileSave from 'custom-plugins/android/file-save';
import { StaticCanvas, Image, Text, Group } from 'fabric';
import * as moment from 'moment';
import { catchError, finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  AttendeeInformation,
  CalendarEvent,
  DATETIMEFORMART,
  EventRequest,
  FloorPlanInfo,
  ForwardEventRequest,
  reminderMinutes,
  showAsOptions,
  UnleashFeatureFlags,
} from 'src/app/interfaces/interfaces';
import { AddAttendeeComponent } from '../add-attendee/add-attendee.component';
import { AddResourceComponent } from '../add-resource/add-resource.component';
import { ProposeNewTimeComponent } from '../propose-new-time/propose-new-time.component';
import { StringInfoMessageService } from 'src/app/services/string-info-message/string-info-message.service';
import { UnleashService } from 'src/app/services/unleash/unleash.service';
import { ApiService } from 'src/app/services/api-services/api.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { FloorplanStore } from 'src/app/stores/floorplan.store';
import { PlacesStore } from 'src/app/stores/places.store';
import { BookingsStore } from 'src/app/stores/bookings.store';
import { UserStore } from 'src/app/stores/user.store';
import { FloorplanSearchService } from '../search/floorplan-search.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { IonModal } from '@ionic/angular/common';
import {
  getCheckoutDelegates,
  isCheckoutDelegationConfigured,
  reconcileParticipantSelection,
  stripCheckoutDelegateUiState,
} from 'src/app/utils/checkout-delegates';

@Component({
  selector: 'app-view-edit-booking',
  templateUrl: './view-edit-booking.component.html',
  styleUrls: ['./view-edit-booking.component.scss'],
})
export class ViewEditBookingComponent implements OnInit, AfterViewInit {
  @Input() meetingToEdit: CalendarEvent;
  @Input() place: any;
  @Input() attendees: any[];

  get aiChatOpenAccordions(): string[] {
    const values: string[] = [];
    if (this.displayAttendees?.length) values.push('participants');
    if (this.place?.showcaseImages?.length) values.push('images');
    if (
      this.meetingToEdit?.hasDescription ||
      this.meetingToEdit?.hasAttachments
    )
      values.push('description');
    if (this.floorplan) values.push('floorplan');
    if (this.resourceMapUrl) values.push('map');
    return values;
  }

  /** 'ai-chat' renders inline inside the AI chat card — no modal chrome, all accordions open, non-interactive. */
  @Input() mode: 'view' | 'edit' | 'ai-chat' = 'view';
  address: string = '';
  seriesMessage: string = '';
  showAsLabel: string = '';
  reminderLabel: string = '';
  duration: string = '';

  resourceMapUrl: string = '';
  unleashFeatureFlags = UnleashFeatureFlags;
  attachmentsLoading: boolean = false;

  floorplan: FloorPlanInfo = null;
  floorplanCanvas: any;
  floorplanLoaded: boolean = false;
  linkedResources: string[] = [];

  @ViewChild('floorplanCanvas', { static: false })
  floorplanCanvasRef!: ElementRef;

  @ViewChild('proposeNewTimeModal')
  proposeNewTimeModal!: IonModal;

  @ViewChild('proposeNewTimeListModal')
  proposeNewTimeListModal!: IonModal;

  public locale = this.dateTimeFormatService.selectedDateTimeFormat;

  acceptedAttendees: AttendeeInformation[] = [];
  tentativeAttendees: AttendeeInformation[] = [];
  declinedAttendees: AttendeeInformation[] = [];
  pendingAttendees: AttendeeInformation[] = [];

  showAsOptionsArr = showAsOptions;
  reminderOptions = reminderMinutes;
  scheduleMeetingInputValue = {
    localStartTimeValue: '',
    localEndTimeValue: '',
    isAllDay: false,
    subject: '',
    showAs: undefined as number | undefined,
    reminder: 15,
    isOnlineMeeting: false,
    attendees: [] as any[],
  };
  maxAllowedDuration = { hours: [] as number[], minutes: [] as number[] };
  bookingLoading: boolean = false;
  errorMessage: string = '';
  showError: boolean = false;
  disableOnlineMeetingToggle: boolean = false;
  responseLoading: boolean = false;
  selectedResponse: 'accept' | 'tentativelyAccept' | 'decline' | null = null;
  @ViewChild('durationPicker') durationPicker!: IonDatetime;
  durationPickerVisible: boolean = false;

  constructor(
    private modalController: ModalController,
    private stringInfoMessageService: StringInfoMessageService,
    public unleashService: UnleashService,
    private apiService: ApiService,
    private toast: ToastService,
    private floorplanStore: FloorplanStore,
    private floorplanSearchService: FloorplanSearchService,
    private placesStore: PlacesStore,
    private dateTimeFormatService: DatetimeFormatService,
    private bookingsStore: BookingsStore,
    private userStore: UserStore,
    private translateService: TranslateService,
    private actionSheetController: ActionSheetController,
  ) {}

  ngOnInit() {
    if (this.place) {
      this.address = this.stringInfoMessageService.buildAddressString(
        this.place,
      );
    }
    if (
      this.meetingToEdit?.seriesMasterId &&
      this.meetingToEdit?.recurrencePattern
    ) {
      this.seriesMessage =
        this.stringInfoMessageService.seriesEventInformationMessage(
          this.meetingToEdit.recurrencePattern,
          true,
        );
    }
    if (this.meetingToEdit?.showAs != null) {
      const option = showAsOptions.find(
        (o) => o.index === this.meetingToEdit.showAs,
      );
      this.showAsLabel = option?.text || '';
    }
    const reminderValue = this.meetingToEdit?.reminderMinutesBeforeStart;
    const reminderOption = reminderMinutes.find(
      (o) => o.value === reminderValue,
    );
    this.reminderLabel = reminderOption?.text || '';
    this.checkIfAllDay();
    this.calculateDuration();
    this.groupAttendees();
    this.generateResourceMapUrl();
    this.ensurePlaceFloorplanMapped();
    this.resolveFloorplan();
    if (
      this.meetingToEdit?.hasAttachments &&
      !this.meetingToEdit?.attachments
    ) {
      this.getEventAttachment();
    }

    this.initSelectedResponse();
  }

  ngAfterViewInit(): void {
    if (this.mode === 'ai-chat' && this.floorplan) {
      this.renderFloorplanPreview();
    }
  }

  private initSelectedResponse() {
    const currentUserEmail = this.userStore.user()?.mail;
    const userAttendee = this.meetingToEdit?.attendees?.find(
      (a) => a.userPrincipalName === currentUserEmail,
    );
    if (userAttendee) {
      const response = userAttendee.response?.toLowerCase();
      if (response === 'accepted') this.selectedResponse = 'accept';
      else if (response === 'tentativelyaccepted')
        this.selectedResponse = 'tentativelyAccept';
      else if (response === 'declined') this.selectedResponse = 'decline';
      else this.selectedResponse = null;
    }
  }

  private checkIfAllDay() {
    const meetingEnd = moment(this.meetingToEdit.endTime).format(
      DATETIMEFORMART,
    );
    const endOfTheDay = moment(this.meetingToEdit.startTime)
      .endOf('day')
      .format(DATETIMEFORMART);
    if (meetingEnd == endOfTheDay) {
      this.meetingToEdit.isAllDay = true;
    }
  }

  private calculateDuration() {
    if (!this.meetingToEdit?.startTime || !this.meetingToEdit?.endTime) {
      this.duration = '00:00';
      return;
    }
    const start = new Date(this.meetingToEdit.startTime).getTime();
    const end = new Date(this.meetingToEdit.endTime).getTime();
    const diffMs = end - start;
    const totalMinutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    this.duration = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private ensurePlaceFloorplanMapped(): void {
    if (
      this.meetingToEdit?.place?.emailAddress &&
      !this.meetingToEdit.place.floorMapId
    ) {
      this.bookingsStore.mapBookingsToFloorplans([this.meetingToEdit]);
    }

    if (
      this.place?.emailAddress 
      && !this.place.floorMapId 
      && this.place !== this.meetingToEdit?.place
    ) {
      this.bookingsStore.mapBookingsToFloorplans([
        { place: this.place } as CalendarEvent,
      ]);
    }
  }

  private resolveFloorplan() {
    const floorMapId = this.place?.floorMapId;
    if (!floorMapId) return;
    this.floorplan =
      this.floorplanStore
        .allFloorplansList()
        ?.find((fp) => fp._id === floorMapId) || null;
    if (
      this.floorplan?.floorMapImage &&
      !this.floorplan.floorMapImage.startsWith('data')
    ) {
      try {
        const parsed = JSON.parse(this.floorplan.floorMapImage);
        this.linkedResources = parsed.objects?.map((obj) => obj.name) || [];
        this.linkedResources.shift();
      } catch (_) {}
    }
  }

  renderFloorplanPreview(attempt = 0): void {
    if (this.floorplanLoaded || !this.floorplan?.floorMapImage) {
      return;
    }

    const delayMs = attempt === 0 ? 150 : 100;
    setTimeout(() => {
      if (this.floorplanLoaded) {
        return;
      }

      if (!this.floorplanCanvasRef) {
        if (attempt < 4) {
          this.renderFloorplanPreview(attempt + 1);
        }
        return;
      }

      this.floorplanLoaded = true;
      const canvasEl = this.floorplanCanvasRef.nativeElement;

      if (this.floorplan.floorMapImage.startsWith('data')) {
        this.initNormalFloorplanImage(canvasEl);
      } else {
        this.initFloorplanFromJSON(canvasEl);
      }
    }, delayMs);
  }

  private initNormalFloorplanImage(canvasEl: HTMLCanvasElement) {
    this.floorplanCanvas = new StaticCanvas(canvasEl, {
      width: 300,
      height: 180,
    });
    Image.fromURL(this.floorplan.floorMapImage).then((img) => {
      const scaleX = this.floorplanCanvas.width / img.width;
      const scaleY = this.floorplanCanvas.height / img.height;
      const scaleFactor = Math.min(scaleX, scaleY);
      this.floorplanCanvas.setViewportTransform([
        scaleFactor,
        0,
        0,
        scaleFactor,
        this.floorplanCanvas.width - img.width * scaleFactor,
        this.floorplanCanvas.height - img.height * scaleFactor,
      ]);
      img.set({
        left: this.floorplanCanvas.width / 2,
        top: this.floorplanCanvas.height / 2,
        originX: 'center',
        originY: 'center',
        hasControls: false,
        selectable: false,
      });
      this.floorplanCanvas.add(img);
      this.floorplanCanvas.viewportCenterObject(img);
      this.floorplanCanvas.requestRenderAll();
    });
  }

  private async initFloorplanFromJSON(canvasEl: HTMLCanvasElement) {
    try {
      const json = JSON.parse(this.floorplan.floorMapImage);
      json.version = '6.0.0';
      this.floorplanCanvas = new StaticCanvas(canvasEl, {
        width: 300,
        height: 180,
        backgroundColor: '#ffffff',
      });
      await this.floorplanCanvas.loadFromJSON(json);

      const objects = this.floorplanCanvas.getObjects();
      if (objects.length === 0) return;

      const boundingRect = objects.reduce(
        (acc, obj) => {
          const r = obj.getBoundingRect();
          return {
            left: Math.min(acc.left, r.left),
            top: Math.min(acc.top, r.top),
            right: Math.max(acc.right, r.left + r.width),
            bottom: Math.max(acc.bottom, r.top + r.height),
          };
        },
        { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity },
      );

      const bw = boundingRect.right - boundingRect.left;
      const bh = boundingRect.bottom - boundingRect.top;
      const sf = Math.min(
        this.floorplanCanvas.getWidth() / bw,
        this.floorplanCanvas.getHeight() / bh,
      );
      const ox = (this.floorplanCanvas.getWidth() - bw * sf) / 2;
      const oy = (this.floorplanCanvas.getHeight() - bh * sf) / 2;

      objects.forEach((obj) => {
        obj.scaleX *= sf;
        obj.scaleY *= sf;
        obj.left = (obj.left - boundingRect.left) * sf + ox;
        obj.top = (obj.top - boundingRect.top) * sf + oy;
        obj.setCoords();
        this.colorShapeByAvailability(obj);
      });
      this.floorplanCanvas.requestRenderAll();
    } catch (e) {
      console.error('Floorplan canvas init failed:', e);
    }
  }

  private colorShapeByAvailability(obj: any) {
    if (obj.name === 'planImage') return;
    const places = this.placesStore.places();
    const matchedPlace = places.find((p) => p.emailAddress === obj.name);

    if (!matchedPlace) return;

    if (matchedPlace.isAvailable) {
      if (obj.type !== 'group') {
        obj.set({
          fill: 'rgba(40,176,11,0.2)',
          stroke: 'rgba(40,176,11,1)',
          strokeWidth: 8,
        });
        this.addCapacityLabel(obj, matchedPlace, true);
      } else {
        (obj as Group).getObjects()[0]?.set({
          fill: 'rgba(40,176,11,0.2)',
          stroke: 'rgba(40,176,11,1)',
          strokeWidth: 3,
        });
      }
    } else {
      if (obj.type !== 'group') {
        obj.set({
          fill: 'rgba(224,6,28,0.2)',
          stroke: 'rgba(224,6,28,1)',
          strokeWidth: 8,
        });
        this.addCapacityLabel(obj, matchedPlace, false);
      } else {
        (obj as Group).getObjects()[0]?.set({
          fill: 'rgba(224,6,28,0.2)',
          stroke: 'rgba(224,6,28,1)',
          strokeWidth: 3,
        });
      }
    }
  }

  private addCapacityLabel(obj: any, matchedPlace: any, isAvailable: boolean) {
    let capacityText: string;
    if (matchedPlace.type === 'room') {
      capacityText = matchedPlace.capacity?.toString() ?? '-';
    } else {
      capacityText = `${matchedPlace.availableSpace ?? '-'}/${matchedPlace.capacity ?? '-'}`;
    }
    if (matchedPlace.capacity == null || matchedPlace.availableSpace == null) {
      capacityText = '-';
    }

    const text = new Text(capacityText, {
      fill: isAvailable ? 'rgba(40,176,11,1)' : 'rgba(224,6,28,1)',
      originX: 'center',
      originY: 'center',
      fontSize: 12,
      fontFamily: 'Open Sans',
      selectable: false,
    });

    const center = obj.getCenterPoint();
    text.setPositionByOrigin(center, 'center', 'center');
    this.floorplanCanvas.add(text);
    this.floorplanCanvas.bringObjectToFront(text);
  }

  onAccordionChange(event: any) {
    const values = event.detail.value;
    if (Array.isArray(values) && values.includes('floorplan')) {
      this.renderFloorplanPreview();
    }
  }

  joinMeeting() {
    if (this.meetingToEdit?.onlineMeetingUrl) {
      window.open(this.meetingToEdit.onlineMeetingUrl, '_blank');
    }
  }

  async openAttendeesModal() {
    const modal = await this.modalController.create({
      component: AddAttendeeComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      canDismiss: async (data, role) => role != 'gesture',
      componentProps: {
        initialAttendees: this.scheduleMeetingInputValue.attendees,
        startTime: this.scheduleMeetingInputValue.localStartTimeValue,
        endTime: this.scheduleMeetingInputValue.localEndTimeValue,
        bookingType: 'edit',
        showDelegateAction: this.showCheckOutDelegateEditor,
      },
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        this.scheduleMeetingInputValue.attendees =
          reconcileParticipantSelection(
            this.scheduleMeetingInputValue.attendees,
            result.data,
          );
      }
    });

    await modal.present();
  }

  async openForwardAttendeeModal() {
    const modal = await this.modalController.create({
      component: AddAttendeeComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      componentProps: {
        initialAttendees: this.meetingToEdit.attendees,
        startTime: this.meetingToEdit.startTime,
        endTime: this.meetingToEdit.endTime,
        purpose: 'forward',
      },
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        const alreadyAddedUPNs =
          this.meetingToEdit.attendees?.map((attendee) =>
            attendee.userPrincipalName.toLowerCase(),
          ) || [];
        const toBeAddedUPNs = result.data.filter(
          (attendee) =>
            !alreadyAddedUPNs.includes(
              attendee.userPrincipalName.toLowerCase(),
            ),
        );
        if (toBeAddedUPNs.length === 0) return;

        const forwardRequest: ForwardEventRequest = {
          eventId: this.meetingToEdit.id,
          comment: '',
          toRecipients: toBeAddedUPNs.map(
            (attendee) => attendee.userPrincipalName,
          ),
        };

        this.bookingsStore
          .forwardBooking(forwardRequest, 'particpant')
          .subscribe({
            error: () => {
              this.toast.openErrorToastAsync(
                this.translateService.instant('inviteAttendeeError'),
              );
            },
          });
      }
    });

    await modal.present();
  }

  async openAddResourceModal() {
    const modal = await this.modalController.create({
      component: AddResourceComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      componentProps: {
        initialResources: (this.meetingToEdit as any).resources || [],
        startTime: this.meetingToEdit.startTime,
        endTime: this.meetingToEdit.endTime,
      },
    });

    modal.onDidDismiss().then((result) => {
      if (result.data) {
        const alreadyAddedUPNs =
          this.meetingToEdit.attendees?.map((attendee) =>
            attendee.userPrincipalName.toLowerCase(),
          ) || [];
        const toBeAddedUPNs = result.data.filter(
          (resource) =>
            !alreadyAddedUPNs.includes(resource.emailAddress.toLowerCase()),
        );
        if (toBeAddedUPNs.length === 0) return;

        const forwardRequest: ForwardEventRequest = {
          eventId: this.meetingToEdit.id,
          comment: '',
          toRecipients: toBeAddedUPNs.map((resource) => resource.emailAddress),
        };

        this.bookingsStore
          .forwardBooking(forwardRequest, 'resource')
          .subscribe({
            error: () => {
              this.toast.openErrorToastAsync(
                this.translateService.instant('inviteAttendeeError'),
              );
            },
          });
      }
    });

    await modal.present();
  }

  openFloorplan() {
    if (!this.floorplan || !this.place) return;
    this.dismissModal();
    this.floorplanSearchService.openMap(this.place);
  }

  private generateResourceMapUrl() {
    if (!this.place || !this.place.address) {
      this.resourceMapUrl = '';
    } else {
      const addressString = this.stringInfoMessageService.buildAddressString(
        this.place,
      );
      if (!addressString || addressString.trim() === '') {
        this.resourceMapUrl = '';
      } else {
        this.resourceMapUrl = `https://maps.google.com/maps?q=${encodeURIComponent(addressString)}&z=15&output=embed`;
      }
    }
  }

  private groupAttendees() {
    const list = this.attendees || [];
    this.acceptedAttendees = list.filter((a) => a.response === 'Accepted');
    this.tentativeAttendees = list.filter(
      (a) => a.response === 'TentativelyAccepted',
    );
    this.declinedAttendees = list.filter((a) => a.response === 'Declined');
    this.pendingAttendees = list.filter(
      (a) =>
        !a.response || a.response === 'None' || a.response === 'NotResponded',
    );
  }

  getAttendeeStatusColor(attendee: AttendeeInformation): string {
    switch (attendee.response) {
      case 'Accepted':
        return 'var(--ion-color-success)';
      case 'TentativelyAccepted':
        return 'var(--ion-color-warning)';
      case 'Declined':
        return 'var(--ion-color-danger)';
      default:
        return 'var(--ion-color-medium)';
    }
  }

  getAttendeeStatusIcon(attendee: AttendeeInformation): string {
    switch (attendee.response) {
      case 'Accepted':
        return 'checkmark-circle';
      case 'TentativelyAccepted':
        return 'help-circle';
      case 'Declined':
        return 'close-circle';
      default:
        return 'ellipse-outline';
    }
  }

  async dismissModal() {
    await this.modalController.dismiss();
  }

  get displayAttendees(): any[] {
    return this.mode === 'edit'
      ? this.scheduleMeetingInputValue.attendees
      : this.attendees || [];
  }

  get checkoutDelegates(): AttendeeInformation[] {
    return getCheckoutDelegates(this.displayAttendees);
  }

  get showCheckOutDelegateEditor(): boolean {
    return (
      this.mode === 'edit' &&
      isCheckoutDelegationConfigured(this.place)
    );
  }

  get showCheckOutDelegateDetails(): boolean {
    return ( this.checkoutDelegates.length > 0);
  }

  get requiredAttendees(): any[] {
    return this.displayAttendees.filter((a) => !a.isOptional);
  }

  get optionalAttendees(): any[] {
    return this.displayAttendees.filter((a) => a.isOptional);
  }

  attendeeHasName(attendee: AttendeeInformation): boolean {
    return !![attendee.firstName, attendee.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
  }

  getAttendeeName(attendee: AttendeeInformation): string {
    const name = [attendee.firstName, attendee.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    return name || attendee.userPrincipalName;
  }

  get isInvitation(): boolean {
    if (
      !this.meetingToEdit ||
      this.meetingToEdit.isOrganizer ||
      this.meetingToEdit.isCancelled
    ) {
      return false;
    }
    const currentUserEmail = this.userStore.user()?.mail;
    if (!currentUserEmail) {
      return false;
    }
    return (
      this.meetingToEdit.attendees?.some(
        (a) => a.userPrincipalName === currentUserEmail,
      ) ?? false
    );
  }

  selectResponse(response: 'accept' | 'tentativelyAccept' | 'decline') {
    if (this.selectedResponse === response) return;
    this.selectedResponse = response;
    this.confirmSelectedResponse();
  }

  async confirmSelectedResponse() {
    if (!this.selectedResponse || !this.meetingToEdit?.id) return;

    const isSeries = !!this.meetingToEdit?.seriesMasterId;
    if (isSeries) {
      switch (this.selectedResponse) {
        case 'accept':
          await this.acceptInvitation();
          break;
        case 'tentativelyAccept':
          await this.tentativelyAcceptInvitation();
          break;
        case 'decline':
          await this.declineInvitation();
          break;
      }
    } else {
      this.responseLoading = true;
      switch (this.selectedResponse) {
        case 'accept':
          this.bookingsStore
            .acceptInvitation(this.meetingToEdit.id, false)
            .subscribe({
              next: () => {
                this.responseLoading = false;
                this.dismissModal();
              },
              error: (ex) => {
                this.responseLoading = false;
                this.toast.openErrorToastAsync(
                  ex.error?.message || 'createEventGeneralErrorToast',
                );
              },
            });
          break;
        case 'tentativelyAccept':
          this.bookingsStore
            .tentativelyAcceptInvitation(this.meetingToEdit.id, false)
            .subscribe({
              next: () => {
                this.responseLoading = false;
                this.dismissModal();
              },
              error: (ex) => {
                this.responseLoading = false;
                this.toast.openErrorToastAsync(
                  ex.error?.message || 'createEventGeneralErrorToast',
                );
              },
            });
          break;
        case 'decline':
          this.bookingsStore
            .declineInvitation(this.meetingToEdit.id, false)
            .subscribe({
              next: () => {
                this.responseLoading = false;
                this.modalController.dismiss({
                  action: 'declined',
                  eventId: this.meetingToEdit.id,
                });
              },
              error: (ex) => {
                this.responseLoading = false;
                this.toast.openErrorToastAsync(
                  ex.error?.message || 'createEventGeneralErrorToast',
                );
              },
            });
          break;
      }
    }
  }

  async acceptInvitation() {
    const eventId = this.meetingToEdit?.seriesMasterId
      ? await this.chooseSeriesOrOccurrence('accept')
      : this.meetingToEdit.id;
    if (!eventId) return;
    const isSeries = eventId === this.meetingToEdit?.seriesMasterId;
    await this.confirmAndAccept(eventId, isSeries);
  }

  async tentativelyAcceptInvitation() {
    const eventId = this.meetingToEdit?.seriesMasterId
      ? await this.chooseSeriesOrOccurrence('tentativelyAccept')
      : this.meetingToEdit.id;
    if (!eventId) return;
    const isSeries = eventId === this.meetingToEdit?.seriesMasterId;
    await this.confirmAndTentativelyAccept(eventId, isSeries);
  }

  async declineInvitation() {
    const eventId = this.meetingToEdit?.seriesMasterId
      ? await this.chooseSeriesOrOccurrence('decline')
      : this.meetingToEdit.id;
    if (!eventId) return;
    const isSeries = eventId === this.meetingToEdit?.seriesMasterId;
    await this.confirmAndDecline(eventId, isSeries);
  }

  private async chooseSeriesOrOccurrence(
    type: 'accept' | 'tentativelyAccept' | 'decline' | 'edit' | 'delete',
  ): Promise<string | null> {
    const actionSheet = await this.actionSheetController.create({
      header: this.translateService.instant(
        'series.' + type + 'SeriesEventHeaderModal',
      ),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('series.EventOccurrence'),
          role: 'occurrence',
        },
        {
          text: this.translateService.instant('series.EventSeries'),
          role: 'series',
        },
        {
          text: this.translateService.instant('cancelButton'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const result = await actionSheet.onDidDismiss();
    if (result.role === 'occurrence') {
      return this.meetingToEdit.id;
    } else if (result.role === 'series') {
      return this.meetingToEdit.seriesMasterId;
    }
    return null;
  }

  private async confirmAndAccept(eventId: string, isSeries: boolean) {
    const actionSheet = await this.actionSheetController.create({
      header: isSeries
        ? this.translateService.instant('acceptSeriesMeetingQuestion')
        : this.translateService.instant('acceptMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.responseLoading = true;
      this.bookingsStore.acceptInvitation(eventId, isSeries).subscribe({
        next: () => {
          this.responseLoading = false;
          this.dismissModal();
        },
        error: (ex) => {
          this.responseLoading = false;
          this.toast.openErrorToastAsync(
            ex.error?.message || 'createEventGeneralErrorToast',
          );
        },
      });
    }
  }

  private async confirmAndTentativelyAccept(
    eventId: string,
    isSeries: boolean,
  ) {
    const actionSheet = await this.actionSheetController.create({
      header: isSeries
        ? this.translateService.instant(
            'tentativelyAcceptSeriesMeetingQuestion',
          )
        : this.translateService.instant('tentativelyAcceptMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.responseLoading = true;
      this.bookingsStore
        .tentativelyAcceptInvitation(eventId, isSeries)
        .subscribe({
          next: () => {
            this.responseLoading = false;
            this.dismissModal();
          },
          error: (ex) => {
            this.responseLoading = false;
            this.toast.openErrorToastAsync(
              ex.error?.message || 'createEventGeneralErrorToast',
            );
          },
        });
    }
  }

  private async confirmAndDecline(eventId: string, isSeries: boolean) {
    const actionSheet = await this.actionSheetController.create({
      header: isSeries
        ? this.translateService.instant('declineSeriesMeetingQuestion')
        : this.translateService.instant('declineMeetingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: this.translateService.instant('feedback.answers.yes'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('feedback.answers.no'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.responseLoading = true;
      this.bookingsStore.declineInvitation(eventId, isSeries).subscribe({
        next: () => {
          this.responseLoading = false;
          this.modalController.dismiss({
            action: 'declined',
            eventId: eventId,
          });
        },
        error: (ex) => {
          this.responseLoading = false;
          this.toast.openErrorToastAsync(
            ex.error?.message || 'createEventGeneralErrorToast',
          );
        },
      });
    }
  }

  async promptDeleteMeeting(): Promise<void> {
    if (this.meetingToEdit.seriesMasterId) {
      const eventId = await this.chooseSeriesOrOccurrence('delete');
      if (!eventId) return;
      const isSeries = eventId === this.meetingToEdit.seriesMasterId;
      await this.doDeleteBooking(eventId, isSeries);
    } else {
      await this.doDeleteBooking(this.meetingToEdit.id, false);
    }
  }

  private async doDeleteBooking(
    id: string,
    isSeriesAction: boolean = false,
  ): Promise<void> {
    const actionSheet = await this.actionSheetController.create({
      header: isSeriesAction
        ? this.translateService.instant('deleteSeriesBookingQuestion')
        : this.translateService.instant('deleteBookingQuestion'),
      mode: 'ios',
      buttons: [
        {
          text: isSeriesAction
            ? this.translateService.instant('deleteSeriesBooking')
            : this.translateService.instant('deleteBooking'),
          role: 'destructive',
        },
        {
          text: this.translateService.instant('cancelButton'),
          role: 'cancel',
        },
      ],
    });
    await actionSheet.present();
    const condition = await actionSheet.onDidDismiss();
    if (condition.role === 'destructive') {
      this.bookingsStore.deleteBooking(id, isSeriesAction).subscribe({
        next: () => this.dismissModal(),
        error: (ex) =>
          this.toast.openErrorToastAsync(ex.error?.message ?? ex.message),
      });
    }
  }

  async switchToEditMode() {
    if (this.meetingToEdit?.seriesMasterId) {
      const scopeId = await this.chooseSeriesOrOccurrence('edit');
      if (!scopeId) return;
      this.meetingToEdit.editMasterEvent =
        scopeId === this.meetingToEdit.seriesMasterId;
    } else if (this.meetingToEdit) {
      this.meetingToEdit.editMasterEvent = false;
    }

    this.scheduleMeetingInputValue.localStartTimeValue = moment(
      this.meetingToEdit.startTime,
    ).format(DATETIMEFORMART);
    this.scheduleMeetingInputValue.localEndTimeValue = moment(
      this.meetingToEdit.endTime,
    ).format(DATETIMEFORMART);
    this.scheduleMeetingInputValue.showAs =
      this.meetingToEdit.showAs != 0
        ? this.meetingToEdit.showAs
        : this.userStore.userPreferences()?.userSettings?.bookingStatus;
    this.scheduleMeetingInputValue.subject = this.meetingToEdit.subject;
    this.scheduleMeetingInputValue.reminder =
      this.meetingToEdit.reminderMinutesBeforeStart;
    this.scheduleMeetingInputValue.isOnlineMeeting =
      this.meetingToEdit.isOnlineMeeting;
    this.scheduleMeetingInputValue.attendees = this.meetingToEdit.attendees
      ? this.meetingToEdit.attendees.map((attendee) => ({ ...attendee }))
      : [];
    this.disableOnlineMeetingToggle = this.meetingToEdit.isOnlineMeeting;

    this.scheduleMeetingInputValue.isAllDay = this.meetingToEdit.isAllDay;

    this.calculateDateTimeHours(
      this.scheduleMeetingInputValue.localStartTimeValue,
    );
    this.calculateDateTimeMinutes(
      this.scheduleMeetingInputValue.localStartTimeValue,
    );
    this.calculateDurationFromInputs();

    this.errorMessage = '';
    this.showError = false;
    this.mode = 'edit';
  }

  cancelEdit() {
    this.mode = 'view';
    this.calculateDuration();
    if (this.meetingToEdit?.showAs != null) {
      const option = showAsOptions.find(
        (o) => o.index === this.meetingToEdit.showAs,
      );
      this.showAsLabel = option?.text || '';
    }
    const reminderValue = this.meetingToEdit?.reminderMinutesBeforeStart;
    const reminderOption = reminderMinutes.find(
      (o) => o.value === reminderValue,
    );
    this.reminderLabel = reminderOption?.text || '';
    this.errorMessage = '';
    this.showError = false;
  }

  handleTimeChange(
    event: any,
    type: 'localStartTimeValue' | 'localEndTimeValue',
  ) {
    const oldDuration = this.duration;
    this.scheduleMeetingInputValue[type] = event.detail.value;
    if (type === 'localStartTimeValue') {
      const startTime = moment(
        this.scheduleMeetingInputValue.localStartTimeValue,
      );
      if (this.scheduleMeetingInputValue.isAllDay) {
        this.scheduleMeetingInputValue.localStartTimeValue = moment(
          this.scheduleMeetingInputValue.localStartTimeValue,
        )
          .startOf('day')
          .format(DATETIMEFORMART);
        this.scheduleMeetingInputValue.localEndTimeValue = moment(
          this.scheduleMeetingInputValue.localStartTimeValue,
        )
          .add(1, 'day')
          .startOf('day')
          .format(DATETIMEFORMART);
      } else {
        this.scheduleMeetingInputValue.localEndTimeValue = startTime
          .add(oldDuration)
          .format(DATETIMEFORMART);
      }
    }
    this.calculateDurationFromInputs();
    this.calculateDateTimeHours(
      this.scheduleMeetingInputValue.localStartTimeValue,
    );
    this.calculateDateTimeMinutes(
      this.scheduleMeetingInputValue.localStartTimeValue,
    );
  }

  handleAllDayToggle(event: any) {
    this.scheduleMeetingInputValue.isAllDay = event.detail.checked;
    this.scheduleMeetingInputValue.localStartTimeValue = moment(
      this.meetingToEdit.startTime,
    ).format(DATETIMEFORMART);
    if (this.scheduleMeetingInputValue.isAllDay) {
      this.scheduleMeetingInputValue.localStartTimeValue = moment(
        this.scheduleMeetingInputValue.localStartTimeValue,
      )
        .startOf('day')
        .format(DATETIMEFORMART);
      this.scheduleMeetingInputValue.localEndTimeValue = moment(
        this.scheduleMeetingInputValue.localStartTimeValue,
      )
        .add(1, 'day')
        .startOf('day')
        .format(DATETIMEFORMART);
    } else {
      let roundedStartTime = moment();
      const remainder = 5 - (roundedStartTime.minute() % 5);
      roundedStartTime = roundedStartTime.add(remainder, 'minutes');
      this.scheduleMeetingInputValue.localStartTimeValue =
        roundedStartTime.format(DATETIMEFORMART);
      this.scheduleMeetingInputValue.localEndTimeValue = moment(
        this.scheduleMeetingInputValue.localStartTimeValue,
      )
        .add(60, 'minutes')
        .format(DATETIMEFORMART);
    }
    this.calculateDurationFromInputs();
  }

  handleDurationChange(event: any) {
    this.checkIfLastHourWasChosen(event);
    const dur = event.detail.value;
    const durationMoment = moment.duration(dur);
    const startTime = moment(
      this.scheduleMeetingInputValue.localStartTimeValue,
    );
    this.scheduleMeetingInputValue.localEndTimeValue = startTime
      .clone()
      .add(durationMoment)
      .format(DATETIMEFORMART);
    this.duration = dur;
  }

  private calculateDurationFromInputs() {
    const dur = moment.duration(
      moment(this.scheduleMeetingInputValue.localEndTimeValue).diff(
        moment(this.scheduleMeetingInputValue.localStartTimeValue),
      ),
    );
    const hours = (dur.hours() + dur.days() * 24).toString().padStart(2, '0');
    const min = dur.minutes().toString().padStart(2, '0');
    this.duration = hours + ':' + min;
  }

  calculateDateTimeHours(startTime: string) {
    this.maxAllowedDuration.hours = [];
    const startTimeToCompare = moment(startTime);
    const endOfDay = moment(startTime).endOf('day');
    let differenceInMinutes = endOfDay.diff(startTimeToCompare, 'minutes');
    let differenceInHours = Math.floor(differenceInMinutes / 60);
    if (differenceInMinutes % 60 === 0) differenceInHours -= 1;
    for (let i = 0; i <= differenceInHours; i++) {
      this.maxAllowedDuration.hours.push(i);
    }
  }

  calculateDateTimeMinutes(startTime: string) {
    const startTimeToCompare = moment(startTime);
    const endOfDay = moment(startTime).endOf('day');
    const differenceInMinutes =
      endOfDay.diff(startTimeToCompare, 'minutes') % 60;
    this.maxAllowedDuration.minutes = [];
    for (let i = 0; i <= differenceInMinutes; i++) {
      this.maxAllowedDuration.minutes.push(i);
    }
  }

  checkIfLastHourWasChosen(event: any) {
    const duration = event.detail.value.split(':');
    const selectedHour = duration[0];
    if (
      selectedHour ==
      this.maxAllowedDuration.hours[this.maxAllowedDuration.hours.length - 1]
    ) {
      this.calculateDateTimeMinutes(
        this.scheduleMeetingInputValue.localStartTimeValue,
      );
    } else {
      this.maxAllowedDuration.minutes = Array.from({ length: 60 }, (_, i) => i);
    }
  }

  willPresent() {
    this.durationPickerVisible = true;
    Promise.resolve().then(() => {
      if (this.durationPicker) this.durationPicker.value = this.duration;
    });
  }

  didPresent() {
    setTimeout(() => {
      if (this.durationPicker) this.durationPicker.value = this.duration;
    }, 0);
  }

  didDismiss() {
    this.durationPickerVisible = false;
  }

  doEditBooking() {
    this.bookingLoading = true;
    this.errorMessage = '';
    this.showError = false;

    const {
      localStartTimeValue,
      localEndTimeValue,
      subject,
      showAs,
      reminder,
      isOnlineMeeting,
    } = this.scheduleMeetingInputValue;
    const startTime = moment(localStartTimeValue);
    const endTime = moment(localEndTimeValue);

    if (startTime.isSameOrAfter(endTime)) {
      this.errorMessage = 'Start time must be before end time.';
      this.showError = true;
      this.bookingLoading = false;
      return;
    }
    if (endTime.diff(startTime, 'minutes') < 5) {
      this.errorMessage = this.translateService.instant('lessFiveMinutes');
      this.showError = true;
      this.bookingLoading = false;
      return;
    }

    const eventRequest: EventRequest = {
      isOnPrem: this.place?.isOnPrem || false,
      endTime: localEndTimeValue,
      startTime: localStartTimeValue,
      subject: !subject ? this.generateDefaultBookingSubject() : subject,
      reminderMinutesBeforeStart: reminder,
      showAs: showAs,
      timeZoneInfo: Intl.DateTimeFormat().resolvedOptions().timeZone,
      isOnlineMeeting: isOnlineMeeting,
      attendees: stripCheckoutDelegateUiState(
        this.scheduleMeetingInputValue.attendees,
      ),
      placeEmailAddress: this.place?.emailAddress || '',
      recurrencePattern: this.meetingToEdit.editMasterEvent
        ? this.meetingToEdit.recurrencePattern
        : null,
      isAllDay: this.scheduleMeetingInputValue.isAllDay,
    };

    const eventId = this.meetingToEdit.editMasterEvent
      ? this.meetingToEdit.seriesMasterId
      : this.meetingToEdit.id;

    this.bookingsStore
      .editBooking(eventId, !!this.meetingToEdit.editMasterEvent, eventRequest)
      .pipe(
        catchError((ex) => {
          if (ex.error === 'PlaceNotAvailable') {
            this.errorMessage = 'Place was not available at the selected time.';
            this.showError = true;
          } else if (ex.error?.message) {
            this.errorMessage = ex.error.message;
            this.showError = true;
          } else {
            this.toast.openErrorToastAsync('createEventGeneralErrorToast');
          }
          return ex;
        }),
        finalize(() => {
          this.bookingLoading = false;
        }),
      )
      .subscribe(() => {
        this.dismissModal();
      });
  }

  private generateDefaultBookingSubject(): string {
    const isSeries = !!this.meetingToEdit?.seriesMasterId;
    return isSeries ? 'HXA Series Booking' : 'HXA Scheduled Booking';
  }

  private getEventAttachment() {
    this.attachmentsLoading = true;
    this.apiService.getEventAttachment(this.meetingToEdit.id).subscribe({
      next: (res) => {
        res.forEach((att) => {
          if (att.isInline && att.contentId && att.contentBytes) {
            const regex = new RegExp(`cid:${att.contentId}`, 'g');
            const base64Image = `data:${att.contentType};base64,${att.contentBytes}`;
            this.meetingToEdit.description =
              this.meetingToEdit.description.replace(regex, base64Image);
          }
          if (!att.isInline) {
            att.size = this.formatFileSize(att.size);
          }
        });
        this.meetingToEdit.attachments = res.filter((att) => !att.isInline);
        this.attachmentsLoading = false;
      },
      error: (error) => {
        this.toast.openErrorToastAsync(error.message);
        this.attachmentsLoading = false;
      },
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async downloadAttachment(attachment: any) {
    this.attachmentsLoading = true;
    try {
      this.apiService
        .getEventAttachmentFile(this.meetingToEdit.id, attachment.id)
        .subscribe({
          next: (blob: Blob) => {
            this.shareFile(blob, attachment.name, attachment.contentType);
          },
          error: (error) => {
            console.error('Error downloading attachment:', error);
            this.toast.openErrorToastAsync(error.message);
          },
          complete: () => {
            this.attachmentsLoading = false;
          },
        });
    } catch (error) {
      console.error('Error downloading attachment:', error);
      this.attachmentsLoading = false;
    }
  }

  private async shareFile(blob: Blob, fileName: string, contentType: string) {
    try {
      const platform = Capacitor.getPlatform();
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = (reader.result as string).split(',')[1];
        if (platform === 'android') {
          try {
            await FileSave.saveFile({
              filename: fileName,
              mimeType: contentType,
              base64Data: base64Data,
            });
          } catch (error) {
            console.error('Error saving file on Android:', error);
          }
        } else {
          try {
            await FileSharer.share({
              filename: fileName,
              contentType: contentType,
              base64Data: base64Data,
            });
          } catch (error) {
            console.error('Error sharing file:', error);
          }
        }
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error sharing file on mobile:', error);
    }
  }

  protected canDismiss = async (data: unknown, role: string) =>
    role != 'gesture';

  protected get showProposeListButton() {
    return (
      this.meetingToEdit.isOrganizer && this.meetingToEdit.attendees.length > 0
    );
  }

  protected async handleDismissProposeNewTime(event: string) {
    try {
      await this.proposeNewTimeModal.dismiss();
    } catch {}
    if (event === 'proposed') {
      await this.modalController.dismiss();
    }
  }

  protected async handleProposeNewTimeListDismiss(event: string) {
    try {
      await this.proposeNewTimeListModal.dismiss();
    } catch {}
    if (event === 'accepted') {
      await this.modalController.dismiss();
    }
  }

  protected get proposedNewTimeCount() {
    return this.meetingToEdit.attendees.filter((a) => a.proposedNewTime != null)
      .length;
  }
}
